import { Fragment, useState } from "react";
import {
  Link as RouterLink,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  List,
  NativeSelect,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import GroupRounded from "@mui/icons-material/GroupRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import { formatMoney } from "@bw/shared/utils";
import { useAuth } from "@bw/shared/hooks";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { ErrorCard, ListSkeleton } from "../../../common/components/states";
import { PromotionSummaryCard } from "../../../common/components/PromotionSummaryCard";
import { RequestLessonForm } from "../components/RequestLessonForm";
import {
  useCoachProfile,
  useCoachRatings,
  useCoachServices,
} from "../hooks/useCoachDetail";
import { useFacilityName } from "../hooks/useFacilityName";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";
import { readSearchReturn } from "../../../common/searchNavigation";

export function CoachDetailPage() {
  const { user } = useAuth();
  const location = useLocation();
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);
  const searchReturn = readSearchReturn(location.search);
  const { coachProfileId } = useParams<{ coachProfileId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFacilityId = searchParams.get("facility_id") || undefined;
  const profile = useCoachProfile(coachProfileId, queryFacilityId);
  const soleFacilityId = profile.data?.facilities.length === 1 ? profile.data.facilities[0]?.facility_id : undefined;
  const facilityId =
    profile.data?.facilities.find(
      (binding) => binding.facility_id === queryFacilityId,
    )?.facility_id || soleFacilityId;
  const services = useCoachServices(coachProfileId, facilityId);
  const facility = useFacilityName(facilityId);
  const ratings = useCoachRatings(coachProfileId, Boolean(user));
  const [requesting, setRequesting] = useState(searchParams.get("message") === "1");

  if (profile.isLoading)
    return (
      <Page narrow>
        <ListSkeleton label="Loading coach…" rows={3} />
      </Page>
    );
  if (profile.isError || !profile.data)
    return (
      <Page narrow>
        <Stack spacing={1.5}>
          <DetailHeader
            back={{
              to:
                searchReturn ??
                withFacilityDiscoveryReturn(
                  queryFacilityId
                    ? `/facility/${encodeURIComponent(queryFacilityId)}`
                    : "/play?activity=coaching",
                  facilitySearchReturn,
                ),
              label: searchReturn
                ? "Back to search"
                : queryFacilityId
                  ? "Back to facility"
                  : "Back to coaches",
            }}
            title="Coach"
          />
          <ErrorCard
            title="We couldn't load this coach. Please try again."
            onRetry={() => void profile.refetch()}
          />
        </Stack>
      </Page>
    );

  const coach = profile.data;
  const coachingPromotion = facility.data?.promotion_summaries?.find(
    (promotion) => promotion.service === "coaching",
  );
  const binding = coach.facilities.find(
    (item) => item.facility_id === facilityId,
  );
  const ratingAverage =
    ratings.data?.total && ratings.data.items.length === ratings.data.total
      ? (
          ratings.data.items.reduce((sum, item) => sum + item.rating, 0) /
          ratings.data.total
        ).toFixed(1)
      : null;
  const stats = [
    ratingAverage ? { value: ratingAverage, label: "rating" } : null,
    ratings.data?.total
      ? {
          value: String(ratings.data.total),
          label: ratings.data.total === 1 ? "review" : "reviews",
        }
      : null,
    binding?.hourly_rate != null
      ? { value: formatMoney(binding.hourly_rate, binding.currency), label: "per hour" }
      : null,
  ].filter((stat): stat is { value: string; label: string } => stat !== null);
  const headline = [
    facility.data?.name,
    coach.specializations.length ? coach.specializations.join(", ") : null,
  ].filter((part): part is string => Boolean(part));

  return (
    <Page narrow>
      <Stack spacing={2}>
        <DetailHeader
          back={
            searchReturn || facilityId
              ? {
                  to:
                    searchReturn ??
                    withFacilityDiscoveryReturn(
                      `/facility/${encodeURIComponent(facilityId as string)}`,
                      facilitySearchReturn,
                    ),
                  label: searchReturn ? "Back to search" : "Back to facility",
                }
              : null
          }
          actions={<ShareButton title={coach.display_name} />}
        />

        {/* Profile header: photo, name, where and what they coach. */}
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", px: { xs: 0.5, sm: 0 } }}>
          <Avatar
            src={coach.photo_url ?? undefined}
            alt=""
            sx={{ height: 72, width: 72, bgcolor: "action.selected", color: "text.primary", fontWeight: 800, fontSize: "1.375rem" }}
          >
            {initials(coach.display_name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700, overflowWrap: "anywhere" }}>
              {coach.display_name}
            </Typography>
            {headline.length ? (
              <Typography variant="body2" color="text.secondary">
                {headline.map((part, index) => (
                  <Fragment key={part}>
                    {index > 0 ? " · " : null}
                    <span>{part}</span>
                  </Fragment>
                ))}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {stats.length ? (
          <Stack direction="row" sx={{ textAlign: "center" }} aria-label="Coach stats">
            {stats.map((stat) => (
              <Typography
                key={stat.label}
                variant="caption"
                color="text.secondary"
                sx={{
                  flex: 1,
                  whiteSpace: "pre-line",
                  // Value on the first line, its label under it — one text run.
                  "&::first-line": { fontSize: "1.0625rem", fontWeight: 700, color: "text.primary", lineHeight: 1.6 },
                }}
              >
                {`${stat.value}\n${stat.label}`}
              </Typography>
            ))}
          </Stack>
        ) : null}

        {facilityId && coachProfileId && !requesting ? (
          <Button
            onClick={() => setRequesting(true)}
            variant="contained"
            fullWidth
            sx={{ maxWidth: { sm: 360 } }}
          >
            Message coach
          </Button>
        ) : null}

        {coach.bio ? (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, px: { xs: 0.5, sm: 0 }, whiteSpace: "pre-line" }}>
            {coach.bio}
          </Typography>
        ) : null}

        {coach.verified_experience ? (
          <Paper component="section" sx={{ maxWidth: 720, p: 2, borderRadius: "12px" }} variant="outlined">
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Chip color="primary" label="BW verified" size="small" />
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                  Verified coaching experience
                </Typography>
              </Stack>
              <Typography color="text.secondary" variant="body2">
                Based only on club-certified delivery recorded on Badminton Warriors—not self-reported experience.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Chip size="small" label={`${coach.verified_experience.teaching_hours_minimum}+ teaching hours`} variant="outlined" />
                <Chip size="small" label={`${coach.verified_experience.programs_delivered_minimum}+ Programs delivered`} variant="outlined" />
                {coach.verified_experience.learner_reach_minimum ? (
                  <Chip size="small" label={`${coach.verified_experience.learner_reach_minimum}+ learners taught`} variant="outlined" />
                ) : null}
                <Chip size="small" label={`Verified since ${coach.verified_experience.verified_since_year}`} variant="outlined" />
              </Stack>
            </Stack>
          </Paper>
        ) : null}

        {coach.facilities.length > 1 ? (
          <Stack spacing={1} sx={{ maxWidth: 520 }}>
            <FormControl fullWidth size="small">
              <InputLabel htmlFor="coach-venue" variant="standard">
                Venue
              </InputLabel>
              <NativeSelect
                id="coach-venue"
                value={facilityId ?? ""}
                onChange={(event) => {
                  setSearchParams((current) => {
                    current.set("facility_id", event.target.value);
                    return current;
                  });
                  setRequesting(searchParams.get("message") === "1");
                }}
                inputProps={{ "aria-label": "Venue" }}
              >
                <option value="">Choose a venue</option>
                {coach.facilities.map((item) => (
                  <FacilityOption key={item.facility_id} facilityId={item.facility_id} />
                ))}
              </NativeSelect>
            </FormControl>
            {!facilityId ? (
              <Typography variant="body2" color="text.secondary">
                Choose the club where you want to train before messaging this coach.
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {facilityId && coachProfileId && requesting ? (
          <Box id="coach-inquiry">
            <RequestLessonForm
              key={`${coachProfileId}:${facilityId}`}
              coachProfileId={coachProfileId}
              facilityId={facilityId}
            />
          </Box>
        ) : null}

        <Stack component="section" spacing={0.5} aria-labelledby="coach-lessons">
          <SectionHeader id="coach-lessons" title="Lessons" />
          {coachingPromotion ? (
            <Stack spacing={0.75} sx={{ maxWidth: 520 }}>
              <PromotionSummaryCard promotion={coachingPromotion} />
              <Typography variant="caption" color="text.secondary">
                Final eligibility and savings are confirmed in the accepted
                coaching payment review.
              </Typography>
            </Stack>
          ) : null}
          {services.isLoading ? (
            <ListSkeleton label="Loading lessons…" leading="square" />
          ) : null}
          {services.isError ? (
            <ErrorCard
              title="We couldn't load lesson options."
              retryLabel="Retry lessons"
              onRetry={() => void services.refetch()}
            />
          ) : null}
          {services.data?.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 } }}>
              Ask the coach about available training options.
            </Typography>
          ) : null}
          {services.data?.length ? (
            <List disablePadding>
              {services.data.map((service) => (
                <DenseRow
                  key={service.service_id}
                  leading={
                    <Box
                      aria-hidden
                      sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", flexShrink: 0 }}
                    >
                      {service.capacity_max > 1 ? (
                        <GroupRounded fontSize="small" />
                      ) : (
                        <PersonRounded fontSize="small" />
                      )}
                    </Box>
                  }
                  title={service.name}
                  trailing={
                    facilityId && coachProfileId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        aria-label={`Request ${service.name}`}
                        onClick={() => {
                          setRequesting(true);
                          // The inquiry form opens above the lessons; take the player there.
                          window.requestAnimationFrame(() =>
                            document.getElementById("coach-inquiry")?.scrollIntoView({ behavior: "smooth", block: "center" }),
                          );
                        }}
                        sx={{ borderRadius: 999, px: 1.75, flexShrink: 0, color: "text.primary", borderColor: "divider" }}
                      >
                        Request
                      </Button>
                    ) : undefined
                  }
                  subtitle={`${service.duration_minutes} min · ${formatMoney(service.base_price, service.currency)}`}
                />
              ))}
            </List>
          ) : null}
        </Stack>

        {coachProfileId ? (
          <Button
            component={RouterLink}
            to={`/me/coaching-experience?profile=${encodeURIComponent(coachProfileId)}`}
            variant="text"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Is this your club coach profile?
          </Button>
        ) : null}
      </Stack>
    </Page>
  );
}

function FacilityOption({ facilityId }: { facilityId: string }) {
  const facility = useFacilityName(facilityId);
  return <option value={facilityId}>{facility.data?.name ?? "Venue"}</option>;
}


function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
